// Sign-in, and the token every API call carries.
//
// MSAL with redirects — no popups, which conditional access and some browsers block. The
// access token is for SorteraChat's own API; the server exchanges it for an Azure DevOps token
// on its side, so the browser never holds a DevOps credential.

import type { AccountInfo } from "@azure/msal-browser";
import type { PublicConfig } from "./config.ts";

export interface Auth {
  /** Null when signed out — the app then shows the sign-in screen. */
  account: AccountInfo | null;
  signIn(): Promise<void>;
  signOut(): Promise<void>;
  token(): Promise<string | null>;
}

export async function createAuth(config: PublicConfig): Promise<Auth> {
  // Loaded on its own — it is a third of the app's weight.
  const { InteractionRequiredAuthError, PublicClientApplication } = await import("@azure/msal-browser");
  const msal = new PublicClientApplication({
    auth: {
      clientId: config.clientId,
      authority: `https://login.microsoftonline.com/${config.tenantId}`,
      redirectUri: window.location.origin,
      postLogoutRedirectUri: window.location.origin,
    },
    cache: { cacheLocation: "localStorage" },
  });
  await msal.initialize();

  const redirect = await msal.handleRedirectPromise();
  const account = redirect?.account ?? msal.getAllAccounts()[0] ?? null;
  if (account) msal.setActiveAccount(account);

  const scopes = [config.scope];

  return {
    account,
    signIn: () => msal.loginRedirect({ scopes, prompt: "select_account" }),
    signOut: () => msal.logoutRedirect({ account: msal.getActiveAccount() ?? undefined }),
    async token() {
      const active = msal.getActiveAccount();
      if (!active) return null;
      try {
        return (await msal.acquireTokenSilent({ scopes, account: active })).accessToken;
      } catch (error) {
        if (error instanceof InteractionRequiredAuthError) {
          await msal.acquireTokenRedirect({ scopes, account: active });
        }
        throw error;
      }
    },
  };
}
